import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { ExerciseConfigurationPageParams, ExerciseConfigurationPageResult } from './exercise-configuration-interface';
import { DUP_REP_TARGET_GAP, FitnessPlan, getMinTargetRepsForIntensity, getMyoSetsFromSets, getRepTargetForIntensity, getSiblingRepTargetBounds, getSlotPathIntensity, IntensityLevel, Plan_ExercisePath, Plan_SubSlot, TUP_REP_TARGET_GAP } from '@cf-shared/data-model/fitness-plan';
import { getEffectivePlanSetCount, setPlanSetCount } from '@cf-shared/data-model/plan-set-count';
import { subSlotUsesMyoReps } from '@cf-shared/data-model/exercise-myoreps';
import { FitnessState, MyoRepsMode, ExerciseStat, DeclaredState } from '@cf-shared/data-model/user-state';
import { AdvancedMethodsRMTesting, SetType } from '@cf-shared/enums';
import { Exercise, ExerciseDbService, exerciseUsesRMTesting, getReadableSlotNameFromId } from 'services/exercise-db.service';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { getExerciseForSubSlot } from 'services/exercise-switching';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { sum } from 'lodash-es';
import { SettingsService } from 'services/settings.service';
import { TranslatedString } from 'models/translated-string';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { LoggingService } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider, CanLeaveResult } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { CoachExerciseChangeService } from 'services/coach-exercise-change.service';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { CoachView, ExerciseStateEditor } from './exercise-state-editor';

@Component({
  selector: 'page-exercise-configuration',
  templateUrl: 'exercise-configuration-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto' }) }]
})
export class ExerciseConfigurationPage extends CsPage<ExerciseConfigurationPageParams, ExerciseConfigurationPageResult> implements OnDestroy {
  protected LL = LL;
  protected loading = true;
  private docWatcher: DocWatcher;
  private unsavedChangesGuard: UnsavedChangesGuard<ExerciseConfigurationPageResult>;
  /**
   * True once the user has picked "Save" in the leave dialog.
   *
   * This page answers its caller with a `saved` flag, and one caller — the
   * add-an-exercise flow in Edit Workouts — treats `saved: true` as "go ahead
   * and add this exercise to the workout". So the flag must say what the user
   * actually chose. The guard hands back a result on two different paths: when
   * the user picks Save, and when nothing was changed at all. Only the first is
   * a save, and only the first runs the guard's saver, which is where this is
   * set.
   */
  private savedFromLeaveDialog = false;

  protected fitnessPlan: FitnessPlan;
  protected fitnessState: FitnessState;
  protected fitnessPlanInitial: FitnessPlan;
  protected fitnessStateInitial: FitnessState;
  protected declaredState: DeclaredState;

  protected subSlotId: string;
  protected exerciseId: string;
  protected exerciseName: string;
  protected slotName: string;
  protected numSets: number;
  /** the Sets count the page showed when it opened (#6428): a Volume change is judged against it */
  private initialNumSets: number;
  protected myoMode: MyoRepsMode | undefined;
  protected rmTestingMode: AdvancedMethodsRMTesting | undefined;
  protected myorepTextLong: TranslatedString | undefined;
  protected repTargets: {
    intensityLevel: IntensityLevel;
    targetReps: number | undefined;
    label: TranslatedString;
    isEstablished: boolean;
    constraintHint: TranslatedString | undefined;
    validationError: TranslatedString | undefined;
  }[] = [];
  protected hasEstablishedRepTarget = false;
  protected repTargetInfoMessage: TranslatedString | undefined;
  protected dupTupBanner: TranslatedString | undefined;
  private exercise: Exercise;

  /**
   * True once `loadAsync` has set the page up. The page is entered again when a page pushed on top
   * of it closes (the help page behind the help icon, say), and setting it up a second time would
   * take the coach's edits as the starting point: `somethingChanged()` would then answer false and
   * the caller would drop the edit.
   */
  private setUpDone = false;

  /** the exercise editor (#6428); undefined outside the gate, which keeps today's page */
  protected editor: ExerciseStateEditor | undefined;
  /** the Advanced fold that holds the editor's parts; closed when the page opens */
  protected advancedOpen = false;
  /** `ConfigKey.AdvancedExerciseStateEditor`: opens the editor to a user on their own program */
  private editorFlagOn = false;

  MyoRepsMode = MyoRepsMode;
  AdvancedMethodsRMTesting = AdvancedMethodsRMTesting;

  constructor(
    public navCtrl: NavController,
    private exerciseDb: ExerciseDbService,
    private userDb: UserDb,
    private debug: DebugService,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private cdr: ChangeDetectorRef,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    public settings: SettingsService,
    private coachExerciseChange: CoachExerciseChangeService,
    private configDb: ConfigDbService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdr.markForCheck();
      });
    }
    await this.loadAsync();
  }

  async loadAsync() {
    // if the page was set up already, this is a return from a page pushed on top of it: keep the
    // edits and the starting copies as they are
    if (this.setUpDone) {
      this.cdr.markForCheck();
      return;
    }
    this.setUpDone = true;

    // local params - access via data.local
    this.fitnessPlan = this.navParams.data.local?.fitnessPlan;
    this.fitnessState = this.navParams.data.local?.fitnessState;
    this.subSlotId = this.navParams.data.subSlotId;

    this.fitnessPlanInitial = deepClone(this.fitnessPlan);
    this.fitnessStateInitial = deepClone(this.fitnessState);
    try {
      this.declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      await this.loadExerciseDataAsync();
      this.initialNumSets = this.numSets;
      this.editorFlagOn = await this.readEditorFlagAsync();
      if (this.isCoachGateOpen()) {
        await this.loadEditorAsync();
      }
    } catch (err) {
      this.closeAfterFailedLoad(err);
      return;
    }

    // Build the guard now the two documents hold exactly what the caller handed
    // over. From here on anything the user changes is unsaved work, and the
    // guard asks about it before the page closes (#5058). The page has no
    // database write of its own — "saving" here means handing the edited
    // documents back to the page that opened this one — so the guard's saver
    // does nothing and getResult carries the answer.
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard<ExerciseConfigurationPageResult>(
      [this.fitnessPlan, this.fitnessState],
      // nothing to write: the caller applies the edited documents. Running at
      // all is the signal that the user picked Save.
      async () => { this.savedFromLeaveDialog = true; },
      {
        getResult: (): ExerciseConfigurationPageResult => ({
          modified: this.savedFromLeaveDialog && this.somethingChanged(),
          saved: this.savedFromLeaveDialog,
          fitnessPlan: this.fitnessPlan,
          fitnessState: this.fitnessState,
          coachEdit: this.savedFromLeaveDialog ? this.editor?.editResult(this.somethingChanged()) : undefined,
        }),
      },
    );

    this.loading = false;
    this.cdr.markForCheck();
  }

  /** The exercise could not be read (a timed-out catalog fetch, say). Showing a stand-in
   *  exercise instead would let the user change, and save, another exercise's settings
   *  (df-a89f1ea2145e), so say so and close. Nothing has been edited yet, and the leave
   *  guard is not built yet, so nothing is lost. */
  private closeAfterFailedLoad(err: unknown) {
    // the app shutting its own database down (sign-out, say) is expected and not a fault;
    // every other failure is reported
    if (!isDatabaseTornDown(err)) {
      this.logging.trackSilentProblem('ExerciseConfigurationLoadFailed', { subSlotId: this.subSlotId, error: describeErrorForLog(err) });
    }
    this.loading = false;
    this.cdr.markForCheck();
    // not awaited: this runs inside the page's entry hook, and the alert answers only when a
    // person closes it. Close the page after it closes however it closed, unless the page
    // has gone away meanwhile (same shape as the workout page's could-not-load alert)
    void this.alertCtrl.showAlertAsync({
      title: LL.exerciseConfiguration.loadFailed.title(),
      message: LL.exerciseConfiguration.loadFailed.message(),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    }).then(() => {
      if (!this.viewDestroyed) {
        void this.handleCancelAsync();
      }
    });
  }

  private async loadExerciseDataAsync() {
    const subSlot = this.fitnessPlan.subSlots[this.subSlotId];
    const slot = await this.exerciseDb.slotForIdAsync(subSlot.slotId, this.docWatcher);
    const exercise = await getExerciseForSubSlot(
      subSlot,
      subSlot.exercise_id,
      this.exerciseDb,
      this.userDb,
      this.fitnessState,
      this.fitnessPlan,
      this.debug.getNowLocal().toUTC(),
      this.docWatcher
    );

    this.exercise = exercise;
    this.exerciseId = exercise._id;
    this.exerciseName = exercise.name;
    this.slotName = slot ? getReadableSlotNameFromId(slot, this.subSlotId) : undefined;
    // show the count the user's next workout really holds, which includes a set they
    // gained by accepting the app's volume suggestion during training (#4523). Before
    // this the field showed the base count alone, so it contradicted their workouts —
    // and typing the number they actually perform added the accepted set on top again.
    const usesMyoReps = subSlotUsesMyoReps(subSlot, exercise, this.declaredState, this.fitnessState);
    this.numSets = getEffectivePlanSetCount(subSlot, usesMyoReps) || 1;

    const exerciseStats = this.fitnessState.exerciseStats[this.exerciseId] || {} as ExerciseStat;

    // if myo reps not allowed
    if (!exercise.allowMyoReps) {
      this.myoMode = undefined;
    } else {
      this.myoMode = exerciseStats.preferMyoReps || MyoRepsMode.Auto;
    }

    // rm testing mode
    this.rmTestingMode = exerciseStats.preferRMTesting || AdvancedMethodsRMTesting.Automatic;

    // rep target data
    this.loadRepTargetData(subSlot);

    this.updateMyoRepText();
  }

  private loadRepTargetData(subSlot: Plan_SubSlot) {
    const paths = subSlot.exercisePaths || [];
    const exerciseStat = this.fitnessState.exerciseStats[this.exerciseId];

    // if paths exist, map them to rep target inputs.
    // three-deep fallback during Phase 2A migration window: ExerciseStat preference (post-Phase-2),
    // path.originalTargetReps (legacy, pre-migration), then path.target_reps (system default).
    // Phase 2B will drop the middle term together with the deprecated field.
    if (paths.length > 0) {
      this.repTargets = paths.map(path => ({
        intensityLevel: path.intensityLevel,
        targetReps: exerciseStat?.preferRepTargetByIntensity?.[path.intensityLevel] ?? path.originalTargetReps ?? path.target_reps,
        label: this.getIntensityLabel(path.intensityLevel, paths.length),
        isEstablished: path.target_reps !== undefined,
        constraintHint: undefined,
        validationError: undefined,
      }));
    } else if (subSlot.rebuildPathsForIntensity !== undefined && !exerciseUsesRMTesting(this.exercise, this.declaredState, this.fitnessState)) {
      // paths not yet built (lazy init), but for non-RM exercises we can calculate the rep target
      const tempPath = { intensityLevel: subSlot.rebuildPathsForIntensity } as Plan_ExercisePath;
      const targetReps = getRepTargetForIntensity(getSlotPathIntensity(subSlot, tempPath));
      this.repTargets = [{
        intensityLevel: subSlot.rebuildPathsForIntensity,
        targetReps,
        label: LL.exerciseConfiguration.labels.repTarget(),
        isEstablished: true,
        constraintHint: undefined,
        validationError: undefined,
      }];
    } else {
      // if no paths yet, show single placeholder entry
      this.repTargets = [{
        intensityLevel: IntensityLevel.Initial,
        targetReps: undefined,
        label: LL.exerciseConfiguration.labels.repTarget(),
        isEstablished: false,
        constraintHint: undefined,
        validationError: undefined,
      }];
    }

    // check if any rep target is established
    this.hasEstablishedRepTarget = paths.some(p => p.target_reps !== undefined);

    // compute per-row constraint hints for DUP/TUP based on current sibling values
    this.updateConstraintHints();

    // pick banner variant when the exercise is on DUP or TUP (more than one path)
    this.updateDupTupBanner();

    // set the info message
    this.updateRepTargetInfoMessage();
  }

  /** Recompute every row's constraint hint. Editing one DUP/TUP target shifts the
   * sibling constraints (e.g. raising heavy raises light's minimum), so we have
   * to refresh all rows whenever any one changes. */
  private updateConstraintHints() {
    const subSlot = this.fitnessPlan.subSlots[this.subSlotId];
    const paths = subSlot?.exercisePaths || [];
    for (const row of this.repTargets) {
      row.constraintHint = this.computeConstraintHint(row.intensityLevel, paths);
    }
  }

  /** For a given DUP/TUP path, format an inline hint like "Must be at least N reps"
   * derived from sibling target_reps via the shared gap helper. Returns undefined for
   * single-path Initial (no siblings) or when siblings haven't established target_reps yet
   * (no "Must be ≥ undefined" rendered). Reads from path.target_reps — same source the
   * validator enforces against — so hint and validator never disagree. */
  private computeConstraintHint(intensityLevel: IntensityLevel, paths: Plan_ExercisePath[]): TranslatedString | undefined {
    const { min, max } = this.getSiblingGapConstraint(intensityLevel, paths);
    if (min !== undefined && max !== undefined) {
      return LL.exerciseConfiguration.repTargetHint.between({ min, max });
    }
    if (min !== undefined) {
      return LL.exerciseConfiguration.repTargetHint.atLeast({ min });
    }
    if (max !== undefined) {
      return LL.exerciseConfiguration.repTargetHint.atMost({ max });
    }
    return undefined;
  }

  /** Pick the DUP/TUP explainer banner shown above the rep-target rows. DUP and TUP
   * intensity levels never mix on one subslot (the rebuild produces all-DUP or all-TUP),
   * so a single sibling check classifies the variant. Cleared for single-path layouts. */
  private updateDupTupBanner() {
    if (this.repTargets.length <= 1) {
      this.dupTupBanner = undefined;
      return;
    }
    const isDup = this.repTargets.some(r =>
      r.intensityLevel === IntensityLevel.DUPheavy || r.intensityLevel === IntensityLevel.DUPlight);
    this.dupTupBanner = isDup
      ? LL.exerciseConfiguration.repTargetHint.dupBanner({ gap: DUP_REP_TARGET_GAP })
      : LL.exerciseConfiguration.repTargetHint.tupBanner({ gap: TUP_REP_TARGET_GAP });
  }

  /** The page's one reader of the DUP/TUP sibling-gap rules. Returns the min/max bounds
   * a target_reps value must satisfy at the given intensityLevel given current sibling
   * values. Used by both `validateRepTargetGaps` (write path) and `computeConstraintHint`
   * (read path) so the inline hint always describes the same boundary the validator
   * enforces.
   *
   * A sibling's effective rep target reads `ExerciseStat.preferRepTargetByIntensity`
   * FIRST, then falls back to `path.target_reps` — the same primary source the rep-target
   * INPUTS display (see loadRepTargetData). They can diverge when a preference was set
   * without rebuilding the path yet (e.g. the workout-plan scanner writes the preference
   * and defers the path rebuild): reading the path alone made the hint contradict the
   * shown value (light shows 14, but a stale path target_reps=13 yielded "heavy ≤ 9"
   * instead of "≤ 10"). Reading the preference first keeps hint, validator, and the
   * displayed inputs in agreement.
   *
   * The gap rules themselves live in `getSiblingRepTargetBounds` in
   * `@cf-shared/data-model/fitness-plan`, which also returns the gap size so the toast
   * and banner strings state the same number the validator enforces.
   */
  private getSiblingGapConstraint(intensityLevel: IntensityLevel, paths: Plan_ExercisePath[]): { min?: number; max?: number; gap?: number } {
    const preferByLevel = this.fitnessState.exerciseStats[this.exerciseId]?.preferRepTargetByIntensity;
    const getSiblingReps = (sibLevel: IntensityLevel): number | undefined =>
      preferByLevel?.[sibLevel] ?? paths.find(p => p.intensityLevel === sibLevel)?.target_reps;
    return getSiblingRepTargetBounds(intensityLevel, getSiblingReps);
  }

  /** True if any rep-target row currently holds a validation error — used to disable Save. */
  protected get hasValidationError(): boolean {
    return this.repTargets.some(r => !!r.validationError) || !!this.editor?.hasValidationError;
  }

  /** Pure validator used by both the write path (`handleRepTargetChange`) and the
   * disable-Save guard (`hasValidationError`). Returns the localized error string for
   * an invalid input, or null if valid. Covers the min-reps-per-intensity rule and the
   * DUP/TUP sibling-gap rules (delegated to `validateRepTargetGaps`). Reads sibling
   * targets from the passed-in paths array — pass `subSlot.exercisePaths` to validate
   * against the current saved plan state. */
  private validateRepTarget(
    intensityLevel: IntensityLevel,
    newValue: number | undefined,
    paths: Plan_ExercisePath[],
  ): TranslatedString | null {
    if (newValue === undefined || isNaN(newValue) || newValue < 1) {
      return LL.exerciseConfiguration.toast.repTargetRequired();
    }
    // the same rep floor the workout and progression code enforces. this screen used to keep its own
    // copy of the table with a different fallback for the unmapped levels (Initial, Intensified): it
    // allowed 3 where every other reader allows 4, so a target this screen accepted was silently
    // raised by the next rep test. df-ad78490b80a9.
    const minReps = getMinTargetRepsForIntensity(intensityLevel);
    if (newValue < minReps) {
      return LL.exerciseConfiguration.toast.repTargetTooLow({ min: minReps });
    }
    return this.validateRepTargetGaps(intensityLevel, newValue, paths);
  }

  private getIntensityLabel(intensityLevel: IntensityLevel, pathCount: number): TranslatedString {
    // for single path, use generic "Rep Target" label
    if (pathCount === 1) {
      return LL.exerciseConfiguration.labels.repTarget();
    }

    // for DUP/TUP, use specific labels
    switch (intensityLevel) {
      case IntensityLevel.DUPheavy:
      case IntensityLevel.TUPheavy:
        return LL.exerciseConfiguration.labels.heavyRepTarget();
      case IntensityLevel.DUPlight:
      case IntensityLevel.TUPlight:
        return LL.exerciseConfiguration.labels.lightRepTarget();
      case IntensityLevel.TUPmedium:
        return LL.exerciseConfiguration.labels.mediumRepTarget();
      default:
        return LL.exerciseConfiguration.labels.repTarget();
    }
  }

  // reads the exercise the page loaded rather than reading it again: a second read could
  // fail where the first succeeded, and this is started without anybody awaiting it
  private updateMyoRepText() {
    const exercise = this.exercise;
    this.myorepTextLong = undefined;
    // if myo reps allowed
    if (this.myoMode !== undefined) {
      // myo-rep scheduling reads the plan's own count, never the displayed one: for a
      // myo-rep exercise defaultVolume_sets is a combined classic-equivalent volume, and
      // that is the unit both subSlotUsesMyoReps and getMyoSetsFromSets expect.
      const subSlot = this.fitnessPlan.subSlots[this.subSlotId];
      const baseSets = subSlot.defaultVolume_sets || 1;
      if (subSlotUsesMyoReps(subSlot, exercise, this.declaredState, this.fitnessState)) {
        const miniSetsPerSet: number[] = getMyoSetsFromSets(baseSets);
        if (sum(miniSetsPerSet) > 0) {
          const numMyoSets = miniSetsPerSet.length;
          if (numMyoSets === 1) {
            this.myorepTextLong = LL.exerciseConfiguration.myoRepsVolumeOneSeries({ minisets: miniSetsPerSet[0] });
          } else {
            this.myorepTextLong = LL.exerciseConfiguration.myoRepsVolumeMultipleSeries({ count: numMyoSets, minisets: miniSetsPerSet.join('/') });
          }
        }
      }
    }
  }

  handleMyoChange() {
    let exerciseStats = this.fitnessState.exerciseStats[this.exerciseId] || {} as ExerciseStat;
    exerciseStats.preferMyoReps = this.myoMode;
    this.fitnessState.exerciseStats[this.exerciseId] = exerciseStats;
    this.updateMyoRepText();
    this.cdr.markForCheck();
  }

  handleRMTestingChange() {
    let exerciseStats = this.fitnessState.exerciseStats[this.exerciseId] || {} as ExerciseStat;
    exerciseStats.preferRMTesting = this.rmTestingMode;
    this.fitnessState.exerciseStats[this.exerciseId] = exerciseStats;

    // update rep target info message based on new RM testing setting
    this.updateRepTargetInfoMessage();

    this.cdr.markForCheck();
  }

  private updateRepTargetInfoMessage() {
    // if rep target not yet established, show appropriate info message
    if (!this.hasEstablishedRepTarget) {
      const usesRMTesting = exerciseUsesRMTesting(this.exercise, this.declaredState, this.fitnessState);
      this.repTargetInfoMessage = usesRMTesting
        ? LL.exerciseConfiguration.repTargetInfoRMTesting()
        : LL.exerciseConfiguration.repTargetInfo();
    } else {
      this.repTargetInfoMessage = undefined;
    }
  }

  handleNumSetsChange() {
    // the number typed here is the number of sets the user wants to perform, so it
    // becomes the plan's own count and any volume adjustment agreed during training is
    // cleared. Writing the base alone left that adjustment to be added on top, which is
    // how a user who typed the 3 she performs ended up with workouts of 4 (#4523).
    setPlanSetCount(this.fitnessPlan.subSlots[this.subSlotId], this.numSets);
    // #6428: a coach's Volume change answers a waiting volume question. The client's app clears
    // the card; the coach's device writes only the plan (design-docs/coach-exercise-editing.md)
    this.editor?.onNumSetsChanged();
    this.updateMyoRepText();
    this.cdr.markForCheck();
  }

  handleRepTargetChange(index: number) {
    const subSlot = this.fitnessPlan.subSlots[this.subSlotId];
    const repTarget = this.repTargets[index];
    const newValue = repTarget.targetReps;

    // validate against the current paths (sibling gap rules read their state from here).
    // run BEFORE any state mutation so invalid input doesn't create a stub path or touch ExerciseStat.
    const error = this.validateRepTarget(repTarget.intensityLevel, newValue, subSlot.exercisePaths);
    if (error) {
      repTarget.validationError = error;
      this.updateConstraintHints();
      this.cdr.markForCheck();
      return;
    }

    let path = subSlot.exercisePaths.find(p => p.intensityLevel === repTarget.intensityLevel);

    // user edited a rep target for a path that hasn't been materialized yet (new exercise
    // or intensity rebuild pending). without this stub, the working-state target_reps write
    // below would have nowhere to land. the path stays marked for rebuild via
    // rebuildPathsForIntensity; workout-loader's rebuild reads preferRepTargetByIntensity from
    // ExerciseStat (Phase 2 model) so the user's stated value survives the rebuild even though
    // we're not seeding the path here.
    if (!path && subSlot.rebuildPathsForIntensity !== undefined) {
      path = {
        intensityLevel: repTarget.intensityLevel,
        nextWorkoutSetType: exerciseUsesRMTesting(this.exercise, this.declaredState, this.fitnessState)
          ? SetType.RMTest
          : SetType.NonRMTest,
      };
      subSlot.exercisePaths.push(path);
    }

    if (!path) {
      // invariant violation: the template only renders the editable input when the row has
      // `isEstablished: true`, which requires either an existing path or rebuildPathsForIntensity.
      // Reaching here means an editDone fired without either — programmer error, console.error.
      console.error('handleRepTargetChange: no path found and no rebuildPathsForIntensity set', {
        intensityLevel: repTarget.intensityLevel,
        subSlotId: this.subSlotId,
      });
      return;
    }

    // user's stated preference lives on ExerciseStat (Phase 2 model). lazy-init the stat record;
    // weight_unit / estimated1RM_timestamp are populated by other lazy-init paths on first use
    // (e.g. workout-detail.ts seeds weight_unit from logSet.stats on first set log) — codebase
    // precedent at handleMyoChange / handleRMTestingChange above uses the same `|| {} as ExerciseStat` pattern.
    let exerciseStat = this.fitnessState.exerciseStats[this.exerciseId] || {} as ExerciseStat;
    exerciseStat.preferRepTargetByIntensity ||= {};
    if (newValue === undefined) {
      // user cleared the field — revert to system default on next path rebuild
      delete exerciseStat.preferRepTargetByIntensity[repTarget.intensityLevel];
    } else {
      exerciseStat.preferRepTargetByIntensity[repTarget.intensityLevel] = newValue;
    }
    this.fitnessState.exerciseStats[this.exerciseId] = exerciseStat;
    // working-state target_reps stays on path (resets any progression that drifted off the user's stated target)
    path.target_reps = newValue;
    repTarget.validationError = undefined;
    // editing one DUP/TUP value shifts the sibling constraints — recompute all hints
    this.updateConstraintHints();
    // #6428: right after a switch the weights follow the rep targets, except a weight the coach typed
    this.editor?.onRepTargetChanged();
    this.cdr.markForCheck();
  }

  /** Enforce DUP/TUP sibling-gap rules. Bounds come from the shared
   * `getSiblingGapConstraint` helper; this function maps a violation onto the
   * intensity-specific toast key, passing the gap so the message's contextual
   * "(heavy + 4)" / "(light - 3)" hint states the rule actually enforced. */
  private validateRepTargetGaps(intensityLevel: IntensityLevel, newValue: number, paths: Plan_ExercisePath[]): TranslatedString | null {
    const { min, max, gap } = this.getSiblingGapConstraint(intensityLevel, paths);
    if (min === undefined && max === undefined) {
      return null;
    }
    // unreachable: getSiblingRepTargetBounds returns a gap whenever it returns a bound
    if (gap === undefined) {
      throw new Error(`validateRepTargetGaps: bounds without a gap for intensity ${intensityLevel}`);
    }
    if (min !== undefined && newValue < min) {
      return this.getGapMinViolationMessage(intensityLevel, min, gap);
    }
    if (max !== undefined && newValue > max) {
      return this.getGapMaxViolationMessage(intensityLevel, max, gap);
    }
    return null;
  }

  private getGapMinViolationMessage(intensityLevel: IntensityLevel, min: number, gap: number): TranslatedString {
    switch (intensityLevel) {
      case IntensityLevel.DUPlight: return LL.exerciseConfiguration.toast.dupLightTooLow({ min, gap });
      case IntensityLevel.TUPlight: return LL.exerciseConfiguration.toast.tupLightTooLow({ min, gap });
      case IntensityLevel.TUPmedium: return LL.exerciseConfiguration.toast.tupMediumTooLow({ min, gap });
      // unreachable: getSiblingGapConstraint only returns `min` for the three intensities above.
      // Throw rather than `return null` so any future change to that helper without updating
      // this switch surfaces immediately instead of silently bypassing validation.
      default: throw new Error(`getGapMinViolationMessage: no message for intensity ${intensityLevel}`);
    }
  }

  private getGapMaxViolationMessage(intensityLevel: IntensityLevel, max: number, gap: number): TranslatedString {
    switch (intensityLevel) {
      case IntensityLevel.DUPheavy: return LL.exerciseConfiguration.toast.dupHeavyTooHigh({ max, gap });
      case IntensityLevel.TUPheavy: return LL.exerciseConfiguration.toast.tupHeavyTooHigh({ max, gap });
      case IntensityLevel.TUPmedium: return LL.exerciseConfiguration.toast.tupMediumTooHigh({ max, gap });
      // unreachable — see getGapMinViolationMessage for rationale
      default: throw new Error(`getGapMaxViolationMessage: no message for intensity ${intensityLevel}`);
    }
  }

  // ── #6428: the exercise editor. Its state and logic live in ExerciseStateEditor; the page
  // decides whether it is shown. Rules: design-docs/coach-exercise-editing.md ──

  /**
   * True when the editor's parts are shown: the caller can save them, this is not an admin's
   * read-only snapshot, the program entry already has its intensity days, and either a coach is
   * managing a client (always) or the user's `AdvancedExerciseStateEditor` switch is on. Asked once, at
   * set-up; everywhere else the page is exactly as it was before #6428.
   */
  private isCoachGateOpen(): boolean {
    const subSlot = this.fitnessPlan?.subSlots?.[this.subSlotId];
    return this.navParams.data.coachEditable === true
      && (!!this.userDb.getCoachManagingClient() || this.editorFlagOn)
      && !this.userDb.isImpersonating()
      && (subSlot?.exercisePaths?.length ?? 0) > 0;
  }

  /** the switch that opens the editor to everyone; only an explicit `true` is on, and a failed read is off */
  private async readEditorFlagAsync(): Promise<boolean> {
    try {
      return (await this.configDb.getConfigValueAsync<unknown>(ConfigKey.AdvancedExerciseStateEditor)) === true;
    } catch (err) {
      if (!isDatabaseTornDown(err)) {
        this.logging.trackSilentProblem('exercise-configuration.editor-flag-unreadable', { error: describeErrorForLog(err) });
      }
      return false;
    }
  }

  /**
   * Sets the editor up. It is kept only once its reads finished, so a read that throws leaves the
   * page without it, and the load failure closes the page as before.
   */
  private async loadEditorAsync() {
    const editor = new ExerciseStateEditor({
      fitnessPlan: this.fitnessPlan,
      fitnessState: this.fitnessState,
      fitnessPlanInitial: this.fitnessPlanInitial,
      fitnessStateInitial: this.fitnessStateInitial,
      subSlotId: this.subSlotId,
      exerciseId: this.exerciseId,
      exercise: this.exercise,
      exerciseName: this.exerciseName,
      initialNumSets: this.initialNumSets,
      numSets: () => this.numSets,
      docWatcher: this.docWatcher,
      reloadRepTargets: subSlot => this.loadRepTargetData(subSlot),
      markForCheck: () => this.cdr.markForCheck(),
    }, {
      userDb: this.userDb,
      exerciseDb: this.exerciseDb,
      debug: this.debug,
      logging: this.logging,
      settings: this.settings,
      coachExerciseChange: this.coachExerciseChange,
    });
    await editor.loadAsync();
    this.editor = editor;
  }

  /** what the editor's parts show; undefined while the editor is not shown */
  protected get coach(): CoachView | undefined {
    return this.editor?.view;
  }

  protected toggleAdvanced() {
    this.advancedOpen = !this.advancedOpen;
    this.cdr.markForCheck();
  }

  somethingChanged(): boolean {
    return !deepCompareEQ(this.fitnessState, this.fitnessStateInitial) ||
           !deepCompareEQ(this.fitnessPlan, this.fitnessPlanInitial);
  }

  async handleSave() {
    const result: ExerciseConfigurationPageResult = {
      modified: this.somethingChanged(),
      saved: true,
      fitnessPlan: this.fitnessPlan,
      fitnessState: this.fitnessState,
      coachEdit: this.editor?.editResult(this.somethingChanged()),
    };
    // Save hands the edited documents back, so there is nothing left for the
    // guard to ask about — tell it so rather than letting it open a dialog on
    // top of a decision the user has already made.
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync(result);
  }

  handleCancel(event: Event) {
    lockClick(event, this.handleCancelAsync());
  }

  private async handleCancelAsync() {
    const result: ExerciseConfigurationPageResult = {
      modified: false,
      saved: false,
      fitnessPlan: this.fitnessPlanInitial,
      fitnessState: this.fitnessStateInitial,
    };
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync(result);
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the browser back
   * button, Android's hardware back button or a swipe back. Without this, those
   * three ways out threw away every rep target, set count and myo-reps choice
   * the user had just made, with nothing asked (#5058).
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<ExerciseConfigurationPageResult>> {
    // Save and Cancel arm this, having already settled what happens to the edits.
    // Restoring the two documents instead — the cheaper trick the other pages use —
    // would mean overwriting a whole plan and a whole fitness state in place, which
    // is far more than this page should do to silence a dialog.
    if (this.leaveWithoutAsking.consume()) return true;
    // the guard is built once the exercise has loaded; before that there is
    // nothing the user could have changed
    if (!this.unsavedChangesGuard) return true;
    return this.unsavedChangesGuard.confirmLeaveAsync();
  }

  getVolumeLabel(): TranslatedString {
    // if myo rep text exists, use "Equivalent Volume"
    if (this.myorepTextLong) {
      return LL.exerciseConfiguration.labels.equivalentVolume();
    }
    return LL.exerciseConfiguration.labels.volume();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }
}
